import {
    AppStoreTitles,
    getLaunchPageTitles,
    getNativeTitles,
    MessageExtensionTitles,
} from './data';
import { M365AcquisitionsInvariants } from './data/M365AcquisitionsInvariants';
import { getBposNavBarData, getBposNavBarDataAsync } from 'owa-bpos-store/lib/bposNavBarData';
import { getPinnedAppBarTitles } from './pinnedApps/getPinnedAppBarTitles';
import { BootIndicatorTitle } from './data/titles/BootIndicatorTitle';
import { isMOSLaunchPagesEnabled } from './utils/isMOSLaunchPagesEnabled';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { getBootM365AcquisitionFromStartupData } from './utils/getBootM365AcquisitionFromStartupData';
import { getAppBposData } from './utils/getAppBposData';
import { isAppBarPinnedAppsEnabled } from './pinnedApps/isAppBarPinnedAppsEnabled';
import { isMOS3AppServiceAvailable } from './utils/isMOS3AppServiceAvailable';
import type { StrictAcquisition, StrictM365AcquisitionsQuery, StrictTitle } from './types';
import { ImmersiveBizChatAppId, M365AppId } from './data/M365AppId';
import type { MailboxInfo } from 'owa-client-types';
import { isConsumer, getLinkedLicensingMailboxInfoOrSelf } from 'owa-session-store';
import { isServiceRequestSupportedForMailbox } from 'owa-service/lib/utils/isServiceRequestSupportedForMailbox';
import { logGreyError } from 'owa-analytics';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getCachedBizchatAcquisition } from './utils/getCachedBizchatAcquisition';

/**
 * Filter that selects which static titles should be added based on runtime invariants.
 * @param  {StrictTitle} title
 * @returns StrictTitle
 */
function filterValidApps(title: StrictTitle, mailboxInfo?: MailboxInfo): boolean {
    let shouldShowTile = false;
    const appId = title.ingestionId as M365AppId;
    const appInvariant = M365AcquisitionsInvariants[appId];
    if (M365AcquisitionsInvariants.hasOwnProperty(appId) && !!appInvariant) {
        shouldShowTile = appInvariant(mailboxInfo);
    } else {
        // User pinned 3P apps will not have invariants
        shouldShowTile =
            title.ingestionSource === 'UserPinnedAcquisition' ||
            title.ingestionSource === 'BootIndicator';
    }

    if (
        title.categories.includes('m365_app') &&
        !isMOSLaunchPagesEnabled(mailboxInfo) &&
        !getAppBposData(appId, mailboxInfo)?.Url
    ) {
        shouldShowTile = false;
    }

    return !!shouldShowTile;
}

/**
 * Memoizes so we don't unnecessarily need to re-run the filtering
 * and generating functions.
 * @param {MailboxInfo} mailboxInfo
 */
async function getBootM365Titles(mailboxInfo?: MailboxInfo): Promise<StrictTitle[]> {
    // Warm-up license data store to generate license-dependent titles.
    if (
        !getBposNavBarData(mailboxInfo) &&
        !isConsumer(undefined, mailboxInfo) &&
        isServiceRequestSupportedForMailbox(mailboxInfo)
    ) {
        await getBposNavBarDataAsync(
            'getStaticTiles',
            'worker',
            true /* ignore auth error */,
            mailboxInfo
        );
    }

    const bootApps: StrictTitle[] = [
        ...AppStoreTitles,
        ...getLaunchPageTitles(mailboxInfo),
        ...getNativeTitles(mailboxInfo),
        ...MessageExtensionTitles,
        BootIndicatorTitle,
    ].filter(app => filterValidApps(app, mailboxInfo));

    // If MOS3 is unavailable, skip adding 3P app tiles. We don't want to add 3P tiles
    // to the app bar if we can't fetch the full app from MOS3 post-boot.
    if (isAppBarPinnedAppsEnabled(mailboxInfo) && isMOS3AppServiceAvailable(mailboxInfo)) {
        bootApps.push(...getPinnedAppBarTitles(bootApps, mailboxInfo));
    }

    return bootApps;
}

/**
 * Returns the static acquisition list defined in data directory, filtered by each
 * title's invariant.
 * @returns Promise
 */
async function getBootM365Acquisitions(
    mailboxInfoToUse?: MailboxInfo
): Promise<StrictAcquisition[]> {
    const mailboxInfo = getLinkedLicensingMailboxInfoOrSelf(
        mailboxInfoToUse ?? getGlobalSettingsAccountMailboxInfo()
    );
    const staticTitles: StrictTitle[] = await getBootM365Titles(mailboxInfo);

    const staticAcquisitions: StrictAcquisition[] = staticTitles.map((title: StrictTitle) => ({
        __typename: 'M365Acquisition',
        appId: title.ingestionId,
        titleId: title.ingestionId,
        titleDefinition: title,
        acquisitionState: 'Acquired',
        canBeUnacquired: false,
        hasPermissions: false,
        acquisitionContext: 'Tenant',
        manifestId: 'NA',
    }));

    const bizchatIndex = staticAcquisitions.findIndex(
        acquisition =>
            acquisition.appId === M365AppId.CommercialBizChat ||
            acquisition.appId === M365AppId.ConsumerBizChat ||
            acquisition.appId === ImmersiveBizChatAppId
    );

    let cachedMosAcquisitionStartupdata;
    if (isFeatureEnabled('mos-acqdataFromStartup')) {
        try {
            cachedMosAcquisitionStartupdata = getBootM365AcquisitionFromStartupData(
                mailboxInfo ?? getGlobalSettingsAccountMailboxInfo()
            );

            if (cachedMosAcquisitionStartupdata) {
                //need to convert cached bizchat id to the id returned by Mos3 (M365AppId.CommercialBizChat)
                if (cachedMosAcquisitionStartupdata.appId === ImmersiveBizChatAppId) {
                    cachedMosAcquisitionStartupdata.appId = M365AppId.CommercialBizChat;
                }

                if (bizchatIndex !== -1) {
                    staticAcquisitions[bizchatIndex] = cachedMosAcquisitionStartupdata;
                } else {
                    staticAcquisitions.push(cachedMosAcquisitionStartupdata);
                }
            }
        } catch (error) {
            cachedMosAcquisitionStartupdata = undefined;

            // Errors resulting from enabling useUnknownInCatchVariables
            // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'Error | undefined'.
            // @ts-expect-error
            logGreyError('GetCachedBizchatAcquisitionFromStartupDataError', mailboxInfo, error);
        }
    }

    if (bizchatIndex !== -1 && !cachedMosAcquisitionStartupdata) {
        // We attempt to get the BizchatAcquisition from local storage if it was previously set.
        const parsedAcquisition = getCachedBizchatAcquisition(mailboxInfo);

        if (parsedAcquisition) {
            // Replace the existing pinned bizchat acquisition with the cached one, this
            // means that the acquisition existed for the user in a past session, so we
            // only want to use it in that scenario.
            staticAcquisitions[bizchatIndex] = parsedAcquisition;
        }
    }

    return staticAcquisitions;
}

/**
 * Returns the static acquisition list in a M365Acquisitions query format.
 * @returns Promise
 */
function toBootM365AcquisitionsQuery(
    acquisitions: StrictAcquisition[]
): StrictM365AcquisitionsQuery {
    return {
        __typename: 'Query',
        m365Acquisitions: {
            __typename: 'M365AcquisitionsQueryResult',
            edges: acquisitions,
            nextInterval: -1,
            source: 'BOOTSTRAP',
            diagnosticInfo: {
                __typename: 'M365AcquisitionsQueryDiagnosticInfo',
                networkLogs: [],
            },
            settings: {
                __typename: 'M365Settings',
                enableCopilotExtensibility: '',
                promptUserToAcquireCopilot: '',
            },
        },
    };
}

export { toBootM365AcquisitionsQuery, getBootM365Acquisitions, getBootM365Titles };
